import { SystemMailCampaign } from "../model/SystemMailCampaign";
import { fetchWithToken } from "./fetchWithToken";

/** Raw campaign from API (sendTime is string); parse to get Date. */
type SystemMailCampaignRaw = Omit<SystemMailCampaign, "schedules"> & {
    schedules: Array<Omit<SystemMailCampaign["schedules"][number], "sendTime"> & { sendTime: string }>;
};

function parseCampaign(raw: SystemMailCampaignRaw): SystemMailCampaign {
    return {
        ...raw,
        schedules: raw.schedules.map((s) => ({
            ...s,
            sendTime: new Date(s.sendTime),
        })),
    };
}

const getBaseUrl = (customerId: string) => {
    switch (process.env.NODE_ENV) {
        case "production":
            return `/api/system/customers/${customerId}/system-mail-campaigns`;
        case "development":
            return `http://localhost:5000/api/system/customers/${customerId}/system-mail-campaigns`;
        default:
            return `/api/system/customers/${customerId}/system-mail-campaigns`;
    }
};

export async function getSystemMailCampaigns(customerId: string): Promise<SystemMailCampaign[]> {
    const response = await fetchWithToken(getBaseUrl(customerId), { method: "GET" });
    if (!response.ok) throw new Error("Error while loading system mail campaigns");
    const raw: SystemMailCampaignRaw[] = await response.json();
    return raw.map(parseCampaign);
}

export async function getSystemMailCampaign(
    campaignId: string,
    customerId: string
) {
    const response = await fetchWithToken(getBaseUrl(customerId) + `/${campaignId}`, {
        method: "GET",
    });
    if (!response.ok) throw new Error("Error loading system mail campaign");
    const raw: SystemMailCampaignRaw = await response.json();
    return parseCampaign(raw);
}

export async function createSystemMailCampaign(
    campaign: SystemMailCampaign,
    customerId: string
) {
    const response = await fetchWithToken(getBaseUrl(customerId), {
        method: "POST",
        body: JSON.stringify(campaign),
        headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("Error creating system mail campaign");
    return response;
}

export async function updateSystemMailCampaign(
    campaign: SystemMailCampaign,
    campaignId: string,
    customerId: string
): Promise<SystemMailCampaign> {
    const response = await fetchWithToken(getBaseUrl(customerId) + `/${campaignId}`, {
        method: "PUT",
        body: JSON.stringify(campaign),
        headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("Error updating system mail campaign");
    const raw: SystemMailCampaignRaw = await response.json();
    return parseCampaign(raw);
}

export async function updateSystemMailCampaignStatus(
    campaignId: string,
    customerId: string,
    status: string
): Promise<void> {
    const url = `${getBaseUrl(customerId)}/${campaignId}/status?status=${encodeURIComponent(status)}`;
    const response = await fetchWithToken(url, { method: "PATCH" });
    if (!response.ok) throw new Error("Error updating system mail campaign status");
}

export async function deleteSystemMailCampaign(
    campaignId: string,
    customerId: string
): Promise<void> {
    const response = await fetchWithToken(getBaseUrl(customerId) + `/${campaignId}`, {
        method: "DELETE",
    });
    if (!response.ok) throw new Error("Error deleting system mail campaign");
}
